/**
 * useChatListeners Hook
 * 
 * React hook for managing chat-specific listeners.
 * Subscribes when chat opens, unsubscribes when chat closes.
 */

import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import listenerManager from './index';
import QUERYKEYS from '../../api/queryKeys';

/**
 * Hook to manage chat listeners for a specific chat
 * 
 * Uses React Query cache for real-time message updates.
 * 
 * @param {string} chatId - Chat ID
 * @param {Object} space - Spectra object with space_id, owner_id, etc.
 */
export const useChatListeners = (chatId, space) => {
  const navigate = useNavigate();
  const params = useParams();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!chatId || !space) {
      return;
    }

    let cancelled = false;

    // Update context immediately (safe pre-init).
    listenerManager.updateContext({
      params,
      navigate,
      queryClient,
      chatId,
      space,
    });

    // Wait for init before subscribing. Previously this effect early-returned
    // when listenerManager wasn't initialized yet — but its deps ([chatId,
    // space?.space_id]) don't change when init later completes, so it never
    // re-ran and the chat's real-time listeners silently never attached on a
    // fresh app load (e.g. landing directly on a chat URL). That left message/
    // generation updates undelivered until a navigation. whenInitialized()
    // resolves as soon as init completes, closing the race.
    listenerManager.whenInitialized().then(() => {
      if (cancelled) return;
      listenerManager.subscribeChat(chatId, space);
    });

    // Cleanup on unmount or chat change
    return () => {
      cancelled = true;
      listenerManager.unsubscribeChat(chatId, space);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, space?.space_id]); // Re-subscribe if chat or space changes

  // Update context when navigation or queryClient changes (without re-subscribing)
  useEffect(() => {
    if (chatId && space && listenerManager.isInitialized()) {
      listenerManager.updateContext({
        params,
        navigate,
        queryClient,
        chatId,
        space,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, navigate, queryClient, chatId, space?.space_id]);

  // Invalidate messages when user returns to this tab. Persisted status is
  // authoritative if the browser missed a terminal SSE event while hidden.
  // NOTE: only requires chatId — space?.space_id is undefined for personal
  // (non-Spectra) chats (see MainContent's effectiveSpace fallback), and an
  // undefined space_id segment is still a valid, consistent query key. Gating
  // on space?.space_id here used to silently disable this safety net for
  // every personal chat — the most common case.
  useEffect(() => {
    if (!chatId) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      queryClient.invalidateQueries({
        queryKey: [QUERYKEYS.CHAT_MESSAGES, chatId, space?.space_id],
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [chatId, space?.space_id, queryClient]);

  return null;
};

export default useChatListeners;
